import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0C]/95 backdrop-blur-sm z-10 px-4 py-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">Terms of Service</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 max-w-3xl mx-auto">
        <p className="text-white/50 mb-6">Effective date: January 17, 2026</p>
        
        <p className="text-white/80 mb-6">
          These Terms of Service ("Terms") govern your access to and use of{' '}
          <a href="https://cigar-ranker.com" className="text-amber-500 hover:underline">
            https://cigar-ranker.com
          </a>{' '}
          (the "Service"), operated by Cigar Ranker ("Cigar Ranker," "we," "us," or "our").
        </p>
        
        <p className="text-white/80 mb-8">
          By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
        </p>

        {/* Section 1 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">1) Eligibility (21+ Only)</h2>
        <p className="text-white/70 mb-8">
          The Service is intended only for individuals who are 21 years of age or older. By using the Service, you represent that you are at least 21.
        </p>

        {/* Section 2 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">2) The Service</h2>
        <p className="text-white/80 mb-4">
          Cigar Ranker provides tools that allow users to browse, rate, review, and rank cigars and related products/content.
        </p>
        <p className="text-white/70 mb-8">
          We may change, suspend, or discontinue any part of the Service at any time.
        </p>

        {/* Section 3 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">3) Accounts</h2>
        <p className="text-white/80 mb-4">
          To access certain features, you may need to create an account.
        </p>
        <p className="text-white/80 mb-2">You agree to:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li>provide accurate information</li>
          <li>keep your login credentials secure</li>
          <li>be responsible for all activity under your account</li>
        </ul>
        <p className="text-white/70 mb-8">
          We may suspend or terminate accounts that violate these Terms.
        </p>

        {/* Section 4 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">4) User Content</h2>
        <p className="text-white/80 mb-4">
          The Service may allow you to submit content, including ratings, reviews, comments, lists, or other materials ("User Content").
        </p>
        <p className="text-white/80 mb-4">You are responsible for your User Content.</p>
        <p className="text-white/80 mb-2">You agree not to submit User Content that:</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-1 ml-4">
          <li>is illegal, defamatory, harassing, threatening, or abusive</li>
          <li>contains hate speech or encourages violence</li>
          <li>infringes intellectual property or privacy rights</li>
          <li>contains malware, spam, or deceptive content</li>
          <li>includes personal information about others without permission</li>
        </ul>
        
        <h3 className="text-lg font-semibold text-white mb-2">License to Cigar Ranker</h3>
        <p className="text-white/70 mb-8">
          By submitting User Content, you grant Cigar Ranker a non-exclusive, worldwide, royalty-free license to host, store, reproduce, modify (for formatting/display), publish, and display your User Content as needed to operate and improve the Service.
        </p>

        {/* Section 5 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">5) Prohibited Conduct</h2>
        <p className="text-white/80 mb-2">You agree not to:</p>
        <ul className="list-disc list-inside text-white/70 mb-8 space-y-1 ml-4">
          <li>use the Service for unlawful purposes</li>
          <li>attempt to gain unauthorized access to the Service or other users' accounts</li>
          <li>interfere with or disrupt the Service</li>
          <li>scrape, crawl, or harvest data without permission</li>
          <li>use bots or automated scripts to create accounts or submit content</li>
          <li>reverse engineer or attempt to extract source code (except where prohibited by law)</li>
          <li>bypass access controls or security features</li>
        </ul>

        {/* Section 6 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">6) Intellectual Property</h2>
        <p className="text-white/80 mb-4">
          The Service, including its design, features, text, graphics, logos, and software, is owned by Cigar Ranker or its licensors and is protected by intellectual property laws.
        </p>
        <p className="text-white/70 mb-8">
          You may not copy, modify, distribute, sell, or lease any part of the Service unless we give you written permission.
        </p>

        {/* Section 7 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">7) Third-Party Services and Links</h2>
        <p className="text-white/70 mb-8">
          The Service may include links to third-party websites or services. We do not control and are not responsible for third-party content, policies, or practices.
        </p>

        {/* Section 8 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">8) Disclaimers</h2>
        <p className="text-white/80 mb-4">
          The Service is provided on an "AS IS" and "AS AVAILABLE" basis.
        </p>
        <p className="text-white/80 mb-4">
          To the maximum extent permitted by law, we disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
        </p>
        <p className="text-white/70 mb-8">
          Cigar Ranker provides information and user opinions for general informational purposes only. We do not guarantee accuracy, completeness, or usefulness of any content.
        </p>

        {/* Section 9 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">9) Limitation of Liability</h2>
        <p className="text-white/80 mb-4">
          To the maximum extent permitted by law, Cigar Ranker will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or goodwill, arising from or related to your use of the Service.
        </p>
        <p className="text-white/70 mb-8">
          In all cases, Cigar Ranker's total liability for any claim will not exceed $100 USD.
        </p>

        {/* Section 10 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">10) Indemnification</h2>
        <p className="text-white/80 mb-2">
          You agree to indemnify and hold harmless Cigar Ranker from any claims, liabilities, damages, losses, and expenses (including attorneys' fees) arising out of:
        </p>
        <ul className="list-disc list-inside text-white/70 mb-8 space-y-1 ml-4">
          <li>your use of the Service</li>
          <li>your User Content</li>
          <li>your violation of these Terms</li>
        </ul>

        {/* Section 11 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">11) Termination</h2>
        <p className="text-white/80 mb-4">
          You may stop using the Service at any time.
        </p>
        <p className="text-white/70 mb-8">
          We may suspend or terminate your access to the Service at any time if we believe you violated these Terms or if we need to protect the Service or other users.
        </p>

        {/* Section 12 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">12) Changes to These Terms</h2>
        <p className="text-white/70 mb-8">
          We may update these Terms from time to time. The updated version will be posted with a new effective date. Your continued use of the Service after changes means you accept the updated Terms.
        </p>

        {/* Section 13 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">13) Governing Law</h2>
        <p className="text-white/70 mb-8">
          These Terms are governed by the laws of Pennsylvania/United States of America, without regard to conflict of laws principles.
        </p>

        {/* Section 14 */}
        <h2 className="text-xl font-bold text-amber-500 mb-4">14) Contact</h2>
        <p className="text-white/80 mb-2">If you have questions about these Terms, contact:</p>
        <p className="text-white/70 mb-1">Cigar Ranker</p>
        <p className="text-white/70">
          Email:{' '}
          <a href="mailto:pmk9000@protonmail.com" className="text-amber-500 hover:underline">
            pmk9000@protonmail.com
          </a>
        </p>
      </div>

      <BottomNav />
    </div>
  );
};

export default TermsOfServicePage;
